/* =====================================================================
 * DMTypingIndicator — 에이전트 타이핑 인디케이터 (사이드바 동일 팔레트)
 * ===================================================================== */

import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import type { DMAgentType } from '@auw/workspace-utils/types/dm';
import { getAgentInfo } from '@auw/workspace-utils/types/dm';

interface Props {
  agentType: DMAgentType;
}

const DMTypingIndicator: React.FC<Props> = ({ agentType }) => {
  const agent = getAgentInfo(agentType);
  const isSystemApp = agentType === 'system_app';
  const [avatarError, setAvatarError] = useState(false);

  return (
    <Container>
      <TypingAvatar>
        {isSystemApp && !avatarError ? (
          <TypingAvatarImg
            src="/agent_logo.png"
            alt={agent.name}
            onError={() => setAvatarError(true)}
          />
        ) : (
          agent.letter
        )}
      </TypingAvatar>
      <Dots>
        <Dot $delay={0} />
        <Dot $delay={0.2} />
        <Dot $delay={0.4} />
      </Dots>
    </Container>
  );
};

export default DMTypingIndicator;

/* ---- Styled ---- */

const bounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-4px); }
`;

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
`;

const TypingAvatar = styled.div`
  flex-shrink: 0;
  width: ${({ theme }) => theme.dm.msgAvatarSize};
  height: ${({ theme }) => theme.dm.msgAvatarSize};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#3a3a3a' : '#e8e5e1')};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#d4d0cc' : '#5c5550')};
  font-weight: 700;
  font-size: ${({ theme }) => theme.dm.msgAvatarFontSize};
  line-height: 1;
  user-select: none;
  overflow: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: ${({ theme }) => theme.dmMobile.msgAvatarSize};
    height: ${({ theme }) => theme.dmMobile.msgAvatarSize};
    font-size: ${({ theme }) => theme.dmMobile.msgAvatarFontSize};
  }
`;

const TypingAvatarImg = styled.img`
  width: 76%;
  height: 76%;
  object-fit: contain;
  display: block;
`;

const Dots = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.dm.bubbleRadius};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
`;

const Dot = styled.div<{ $delay: number }>`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.textMuted};
  animation: ${bounce} 1.4s infinite ease-in-out;
  animation-delay: ${({ $delay }) => $delay}s;
`;
